/**
 * Asks about hiring BEFORE a deploy is saved (product owner, 21 Oct 2026; before the save since
 * 22 Oct 2026), on the three pages that deploy: Employees ("Deploy Employee to Store": Redeploy and
 * Add store), Stores ("Assign team" › "Deploy Employee") and the Dashboard's redeploy cockpit
 * (Store Performance, MisDeployed and Outside Approved – Redeploy). The page puts `window` once on
 * itself, and its deploy goes:
 *
 *   const hiring = await deployHiring.before({ joined: [...], left });
 *   if (!hiring.deploy) return;              // "Don’t deploy": nothing is saved
 *   let saved = false;
 *   try { await theDeploy(); saved = true; ...the page's own message... }
 *   catch { ...the page's own error... }
 *   finally { if (saved) hiring.saved(); else hiring.failed(); }
 *
 * `before` answers at once with "deploy" when there is nothing to ask: a person not shown
 * hiring, the stores have no live hiring and no hiring request waiting, the reads failed, or
 * they took longer than DEPLOY_HIRING_WAIT_MS. A deploy is never held up by a read. Otherwise it
 * answers once the person has chosen, in the window (DeployHiringCheck), and pressed "Deploy"
 * or "Don’t deploy". `saved()` then does what was chosen (Stop following at once; Move, Cancel,
 * Withdraw and Decline each in its own window) and asks about the store left; `failed()` does
 * nothing to hiring and closes the window.
 *
 * A second `before` while one is still unanswered replaces it, and the first is answered "Don’t
 * deploy". The question belongs to the project it was asked in: when the page moves to another
 * project (or goes) it is answered "Don’t deploy", the window goes, and nothing it does
 * afterwards is told to the page.
 *
 * `onChanged` and `onPlaced` are the page's own refreshes, called as its own hiring windows call
 * them (a link with the server's answer, or nothing when its lists should be read again; a
 * hiring closed as filled placed someone).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { HiringLink } from '../../services/api';
import { DeployHiringCheck, type DeployHiringAsk, type DeployHiringStage } from './DeployHiringCheck';

export type { DeployHiringAsk, DeployHiringJoined, DeployHiringKnown, DeployHiringLeft } from './DeployHiringCheck';
export { DEPLOY_HIRING_WAIT_MS } from './DeployHiringCheck';

/** What `before` answers: whether to save the deploy, and what to tell the window once it is saved or not. */
export interface DeployHiringDecision {
  /** false: "Don’t deploy" was pressed. Save nothing, and leave the deploy as it was on screen. */
  deploy: boolean;
  /** The deploy was saved: what was chosen is done now, then the store left is asked about. */
  saved: () => void;
  /** The deploy was not saved: nothing is done to hiring, and the window goes. */
  failed: () => void;
}

export interface DeployHiringCheckHook {
  /** Before the deploy is saved: ask about hiring at these stores, and say whether to deploy. */
  before: (ask: DeployHiringAsk) => Promise<DeployHiringDecision>;
  /** The window, for the owner to put once on the page. Null while nothing is asked. */
  window: ReactNode;
}

const NOTHING_TO_DO = (): void => {};
/** Nothing was asked (no project): deploy, and nothing follows. */
const DEPLOY_ONLY: DeployHiringDecision = { deploy: true, saved: NOTHING_TO_DO, failed: NOTHING_TO_DO };
/** "Don’t deploy", or a question that went before it was answered. */
const DONT_DEPLOY: DeployHiringDecision = { deploy: false, saved: NOTHING_TO_DO, failed: NOTHING_TO_DO };

export const useDeployHiringCheck = ({
  projectId, userKey, onChanged, onPlaced,
}: {
  projectId: number | null | undefined;
  userKey: string;
  onChanged?: (link?: HiringLink) => void;
  onPlaced?: () => void;
}): DeployHiringCheckHook => {
  const [asked, setAsked] = useState<{ projectId: number; ask: DeployHiringAsk; n: number; stage: DeployHiringStage } | null>(null);
  const asks = useRef(0);
  // The question still waiting for its answer, and how to give it.
  const waiting = useRef<{ n: number; answer: (decision: DeployHiringDecision) => void } | null>(null);
  const projectNow = useRef(projectId);
  projectNow.current = projectId;
  const owner = useRef({ onChanged, onPlaced });
  owner.current = { onChanged, onPlaced };

  /** Answers question `n`, once; a question already answered or replaced is left alone. */
  const answer = useCallback((n: number, decision: DeployHiringDecision) => {
    const was = waiting.current;
    if (!was || was.n !== n) return;
    waiting.current = null;
    was.answer(decision);
  }, []);
  const stageOf = useCallback((n: number, from: DeployHiringStage, to: DeployHiringStage) => {
    setAsked((prev) => (prev && prev.n === n && prev.stage === from ? { ...prev, stage: to } : prev));
  }, []);
  const close = useCallback((n: number) => {
    setAsked((prev) => (prev && prev.n === n ? null : prev));
  }, []);

  // The page moved to another project, or went: the question goes with it, and nothing is deployed for it.
  useEffect(() => {
    setAsked(null);
    return () => {
      const was = waiting.current;
      waiting.current = null;
      was?.answer(DONT_DEPLOY);
    };
  }, [projectId]);

  // Bound to the project on show when the deploy was asked for.
  const before = useCallback((next: DeployHiringAsk) => new Promise<DeployHiringDecision>((resolve) => {
    if (!projectId || !next || next.joined.length === 0) {
      resolve(DEPLOY_ONLY);
      return;
    }
    const was = waiting.current;
    asks.current += 1;
    const n = asks.current;
    waiting.current = { n, answer: resolve };
    was?.answer(DONT_DEPLOY);
    setAsked({ projectId, ask: next, n, stage: 'asking' });
  }), [projectId]);

  const decide = (n: number, deploy: boolean) => {
    if (!deploy) {
      answer(n, DONT_DEPLOY);
      close(n);
      return;
    }
    stageOf(n, 'asking', 'saving');
    answer(n, {
      deploy: true,
      saved: () => stageOf(n, 'saving', 'saved'),
      failed: () => close(n),
    });
  };

  const current = asked && asked.projectId === projectId ? asked : null;
  const shownWindow: ReactNode = current ? (
    <DeployHiringCheck
      key={current.n}
      projectId={current.projectId}
      userKey={userKey}
      ask={current.ask}
      stage={current.stage}
      onDecide={(deploy) => decide(current.n, deploy)}
      onDone={() => close(current.n)}
      // Told to the page only while it still shows the project the question was asked in.
      onChanged={(link) => {
        if (projectNow.current === current.projectId) owner.current.onChanged?.(link);
      }}
      onPlaced={() => {
        if (projectNow.current === current.projectId) owner.current.onPlaced?.();
      }}
    />
  ) : null;

  return { before, window: shownWindow };
};

export default useDeployHiringCheck;
